import { defineStore } from "pinia";
import { ref, computed, reactive, markRaw } from "vue";
import * as api from "../api";
import { groupByDate } from "../lib/groupByDate";
import { wsToNotification } from "./adapters";
import {
  NOTIFICATIONS_PAGE_SIZE,
  NOTIFICATIONS_BROADCAST_CHANNEL,
  NOTIFICATIONS_BROADCAST_EVENTS,
} from "./constants";

const EMPTY_FILTERS = Object.freeze({
  search: "",
  statuses: [],
  types: [],
  categories: [],
  created_at_after: null,
  created_at_before: null,
});

/**
 * Enterprise notification store.
 *
 * Responsibilities:
 *   - Single source of truth for list, counter, categories, filters, expanded state.
 *   - Optimistic updates with rollback for markRead / markAllRead.
 *   - WS deduplication by both `uuid` and `event_uuid`.
 *   - Cross-tab synchronization via BroadcastChannel.
 *
 * UI never mutates state directly — only through actions.
 */
export const useNotificationStore = defineStore("notification", () => {
  // ---------- state ----------
  const byId = ref(new Map());
  const byEventUuid = ref(new Map());
  const order = ref([]);
  const categories = ref([]);
  const unread = ref(0);

  const filters = reactive({ ...EMPTY_FILTERS });
  const pagination = reactive({
    skip: 0,
    limit: NOTIFICATIONS_PAGE_SIZE,
    hasMore: true,
  });

  const loading = ref(false);
  const loadingMore = ref(false);
  const loadingCounter = ref(false);
  const loadingCategories = ref(false);
  const markingAll = ref(false);
  const error = ref(null);

  const expandedUid = ref(null);
  const detailsCache = ref(new Map());

  let listAbortController = null;
  let broadcast = null;

  // ---------- cross-tab ----------
  function ensureBroadcast() {
    if (broadcast || typeof BroadcastChannel === "undefined") return;
    broadcast = markRaw(new BroadcastChannel(NOTIFICATIONS_BROADCAST_CHANNEL));
    broadcast.onmessage = (e) => {
      if (!e?.data || !e.data.type) return;
      const { type, payload } = e.data;
      switch (type) {
        case NOTIFICATIONS_BROADCAST_EVENTS.INGEST:
          ingestRealtime(payload, { broadcast: false });
          break;
        case NOTIFICATIONS_BROADCAST_EVENTS.MARK_READ:
          applyMarkRead(payload.uuid, payload.read_at, { broadcast: false });
          break;
        case NOTIFICATIONS_BROADCAST_EVENTS.MARK_ALL_READ:
          applyMarkAllRead(payload.read_at, { broadcast: false });
          break;
        case NOTIFICATIONS_BROADCAST_EVENTS.RESET:
          reset();
          break;
      }
    };
  }

  function postBroadcast(type, payload) {
    if (!broadcast) return;
    try {
      broadcast.postMessage({ type, payload });
    } catch (_) {
      // ignore: cloning failures don't break the local state
    }
  }

  // ---------- getters ----------
  const items = computed(() =>
    order.value.map((uuid) => byId.value.get(uuid)).filter(Boolean)
  );

  const groupedByDate = computed(() => groupByDate(items.value, getLocale()));

  const unreadByCategory = computed(() => {
    const map = new Map();
    for (const c of categories.value) map.set(c.key, c.count);
    return map;
  });

  const isEmpty = computed(
    () => !loading.value && items.value.length === 0 && !error.value
  );

  // ---------- actions: read ----------
  async function fetchCounter() {
    loadingCounter.value = true;
    try {
      const data = await api.getCounter();
      unread.value = Number(data?.unread ?? 0);
    } catch (e) {
      // counter failures should not block UI
      console.warn("[notifications] fetchCounter failed", e);
    } finally {
      loadingCounter.value = false;
    }
  }

  async function fetchCategories() {
    loadingCategories.value = true;
    try {
      const data = await api.getCategories();
      categories.value = Array.isArray(data?.categories) ? data.categories : [];
    } catch (e) {
      console.warn("[notifications] fetchCategories failed", e);
    } finally {
      loadingCategories.value = false;
    }
  }

  async function fetchList({ append = false } = {}) {
    if (listAbortController) listAbortController.abort();
    listAbortController = new AbortController();

    if (!append) {
      pagination.skip = 0;
      pagination.hasMore = true;
    }

    if (append) loadingMore.value = true;
    else loading.value = true;
    error.value = null;

    try {
      const data = await api.listNotifications(
        {
          ...stripEmpty(filters),
          skip: pagination.skip,
          limit: pagination.limit,
        },
        { signal: listAbortController.signal }
      );
      const incoming = Array.isArray(data?.notifications)
        ? data.notifications
        : [];

      const collected = [];
      for (const item of incoming) {
        if (!item || !item.uuid) continue;
        byId.value.set(item.uuid, item);
        if (item.event_uuid) {
          byEventUuid.value.set(item.event_uuid, item.uuid);
        }
        collected.push(item.uuid);
      }

      if (append) {
        // De-dup: don't double-append uuids already in order
        const seen = new Set(order.value);
        order.value = [
          ...order.value,
          ...collected.filter((id) => !seen.has(id)),
        ];
      } else {
        order.value = collected;
      }

      pagination.skip += collected.length;
      pagination.hasMore = Boolean(data?.has_more);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
      error.value = e;
    } finally {
      loading.value = false;
      loadingMore.value = false;
    }
  }

  async function loadMore() {
    if (loading.value || loadingMore.value || !pagination.hasMore) return;
    return fetchList({ append: true });
  }

  async function fetchOne(uid) {
    try {
      const data = await api.getNotification(uid);
      detailsCache.value.set(uid, data);
      // Backend auto-marks as read; reflect locally.
      applyMarkRead(uid, data?.read_at || new Date().toISOString(), {
        broadcast: true,
      });
      return data;
    } catch (e) {
      error.value = e;
      throw e;
    }
  }

  // ---------- actions: mutations ----------
  async function markReadOne(uidOrEventUuid) {
    // Resolve to the canonical notification uuid: callers may pass either
    // the notification's own uuid or its event_uuid.
    const uuid = byId.value.has(uidOrEventUuid)
      ? uidOrEventUuid
      : byEventUuid.value.get(uidOrEventUuid) || uidOrEventUuid;
    const prev = byId.value.get(uuid);
    if (!prev || prev.read_at) return;

    const now = new Date().toISOString();
    applyMarkRead(uuid, now, { broadcast: true });
    try {
      // GET /{uid} auto-marks the notification as read server-side and
      // returns the fresh row — also populates detailsCache as a bonus.
      const data = await api.getNotification(uuid);
      if (data) detailsCache.value.set(uuid, data);
    } catch (e) {
      // rollback
      applyMarkRead(uuid, null, { broadcast: true });
      throw e;
    }
  }

  async function markAllRead() {
    if (markingAll.value) return;
    markingAll.value = true;

    const snapshot = new Map();
    for (const id of order.value) {
      const item = byId.value.get(id);
      if (item) snapshot.set(id, item.read_at);
    }
    const prevUnread = unread.value;
    const now = new Date().toISOString();
    applyMarkAllRead(now, { broadcast: true });

    try {
      await api.markAllRead();
      await Promise.all([fetchCounter(), fetchCategories()]);
    } catch (e) {
      // rollback
      for (const [id, read_at] of snapshot) {
        const item = byId.value.get(id);
        if (item) byId.value.set(id, { ...item, read_at });
      }
      unread.value = prevUnread;
      error.value = e;
    } finally {
      markingAll.value = false;
    }
  }

  function setExpanded(uid) {
    if (expandedUid.value === uid) {
      expandedUid.value = null;
      return Promise.resolve(null);
    }
    expandedUid.value = uid;
    if (!detailsCache.value.has(uid)) return fetchOne(uid);
    // still ensure it's marked read locally
    const item = byId.value.get(uid);
    if (item && !item.read_at) {
      applyMarkRead(uid, new Date().toISOString(), { broadcast: true });
    }
    return Promise.resolve(detailsCache.value.get(uid));
  }

  function setFilters(partial) {
    let changed = false;
    for (const [k, v] of Object.entries(partial || {})) {
      if (k in filters && filters[k] !== v) {
        filters[k] = v;
        changed = true;
      }
    }
    if (changed) {
      fetchList({ append: false });
    }
  }

  function resetFilters() {
    let changed = false;
    for (const [k, v] of Object.entries(EMPTY_FILTERS)) {
      if (filters[k] !== v) {
        filters[k] = Array.isArray(v) ? [] : v;
        changed = true;
      }
    }
    if (changed) {
      fetchList({ append: false });
    }
  }

  // ---------- actions: realtime ----------
  function ingestRealtime(raw, { broadcast: doBroadcast = true } = {}) {
    ensureBroadcast();
    const normalized = wsToNotification(raw);
    if (!normalized) return;

    const existingId = byEventUuid.value.get(normalized.event_uuid);
    if (existingId) {
      const existing = byId.value.get(existingId);
      if (existing) {
        byId.value.set(existingId, {
          ...existing,
          ...normalized,
          read_at: existing.read_at,
        });
      }
      return;
    }

    // Only show in the active list if user is on page 1.
    // On other pages we still index for dedup; counter still bumps.
    byId.value.set(normalized.uuid, normalized);
    if (normalized.event_uuid)
      byEventUuid.value.set(normalized.event_uuid, normalized.uuid);
    if (pagination.page === 1) {
      order.value.unshift(normalized.uuid);
    }
    if (!normalized.read_at) unread.value += 1;

    if (doBroadcast) {
      postBroadcast(NOTIFICATIONS_BROADCAST_EVENTS.INGEST, raw);
    }
  }

  // ---------- internals ----------
  function applyMarkRead(
    uuid,
    readAt,
    { broadcast: doBroadcast = false } = {}
  ) {
    ensureBroadcast();
    const item = byId.value.get(uuid);
    if (!item) return;
    const wasUnread = !item.read_at;
    if (item.read_at === readAt) return;
    byId.value.set(uuid, { ...item, read_at: readAt });
    if (readAt && wasUnread) {
      unread.value = Math.max(0, unread.value - 1);
      adjustCategoryCount(item.type, -1);
    } else if (!readAt && !wasUnread) {
      unread.value += 1;
      adjustCategoryCount(item.type, +1);
    }
    if (doBroadcast) {
      postBroadcast(NOTIFICATIONS_BROADCAST_EVENTS.MARK_READ, {
        uuid,
        read_at: readAt,
      });
    }
  }

  function adjustCategoryCount(type, delta) {
    if (!type) return;
    const list = categories.value || [];
    const lower = String(type).toLowerCase();
    const cat = list.find(
      (c) => c.key === type || c.key?.toLowerCase() === lower
    );
    if (!cat) return;
    cat.count = Math.max(0, Number(cat.count || 0) + delta);
  }

  function applyMarkAllRead(readAt, { broadcast: doBroadcast = false } = {}) {
    ensureBroadcast();
    for (const id of order.value) {
      const item = byId.value.get(id);
      if (item && !item.read_at) {
        byId.value.set(id, { ...item, read_at: readAt });
      }
    }
    unread.value = 0;
    for (const c of categories.value) c.count = 0;
    if (doBroadcast) {
      postBroadcast(NOTIFICATIONS_BROADCAST_EVENTS.MARK_ALL_READ, {
        read_at: readAt,
      });
    }
  }

  function stripEmpty(obj) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v) && v.length === 0) continue;
      out[k] = v;
    }
    return out;
  }

  function getLocale() {
    return localStorage.getItem("lang") === "kk" ? "kk" : "ru";
  }

  // ---------- lifecycle helpers ----------
  function reset() {
    if (listAbortController) listAbortController.abort();
    byId.value = new Map();
    byEventUuid.value = new Map();
    order.value = [];
    categories.value = [];
    unread.value = 0;
    pagination.skip = 0;
    pagination.hasMore = true;
    expandedUid.value = null;
    detailsCache.value = new Map();
    error.value = null;
  }

  async function bootstrap() {
    ensureBroadcast();
    await Promise.all([fetchCounter(), fetchCategories()]);
  }

  return {
    // state (readonly via convention)
    byId,
    byEventUuid,
    order,
    categories,
    unread,
    filters,
    pagination,
    loading,
    loadingMore,
    loadingCounter,
    loadingCategories,
    markingAll,
    error,
    expandedUid,
    detailsCache,

    // getters
    items,
    groupedByDate,
    unreadByCategory,
    isEmpty,

    // actions
    fetchCounter,
    fetchCategories,
    fetchList,
    loadMore,
    fetchOne,
    markRead: markReadOne,
    markAllRead,
    setExpanded,
    setFilters,
    resetFilters,
    ingestRealtime,
    bootstrap,
    reset,
  };
});
